import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Aggregated dashboard metrics returned by get_dashboard_metrics database function
 */
export interface DashboardAggregates {
  /** Suma de ingresos recibidos (payout) en el mes */
  received_income: number;
  
  /** Suma de ingresos pendientes por recibir en el mes */
  future_income: number;
  
  /** Suma total de gastos en el mes */
  total_expenses: number;
  
  /** 
   * Días reservados en el mes (para cálculo de ocupación).
   * Calculado como suma de (check_out - check_in) de reservaciones activas,
   * ajustado a los límites del mes si la reservación cruza meses.
   * El día de check-out NO se cuenta como ocupado.
   */
  total_reserved_days: number;
  
  /** 
   * Días reservados solo de reservaciones pagadas.
   * Usado para calcular el costo promedio por noche. 
   */
  total_reserved_days_paid_only: number;
  
  /** 
   * Días bloqueados en el mes (mantenimiento, uso personal, iCal, etc.).
   * Se restan de los días disponibles en el cálculo de ocupación.
   */
  total_blocked_days: number;
  
  /** Número de días en el mes seleccionado (28-31) */
  days_in_month: number;
  
  /** 
   * Número de propiedades consideradas en el cálculo.
   * Si hay filtro de propiedades, cuenta solo las seleccionadas.
   */
  properties_count: number;
  
  /** 
   * Total de días posibles considerando la fecha de inicio de operación de cada propiedad.
   * Para cada propiedad: MAX(operation_start_date, primer_día_mes) hasta último_día_mes.
   * Reemplaza el cálculo simple de days_in_month × properties_count.
   * Si una propiedad inició después del mes consultado, contribuye 0 días.
   */
  total_possible_days: number;
  
  /** Conteo de reservaciones con ingresos recibidos */
  received_income_count: number;
  
  /** Conteo de reservaciones con ingresos pendientes */
  future_income_count: number;
  
  /** Conteo total de gastos en el mes */
  expenses_count: number;
  
  /** 
   * Noches disponibles para rentar desde la fecha actual hasta fin de mes.
   * Solo cuenta noches futuras que no están bloqueadas ni reservadas.
   */
  nights_to_rent: number;

  /** Datos para calcular tasa de ocupación próximos 7 días */
  occupancy_rate_7_days: {
    reserved_days: number;
    blocked_days: number;
    possible_days: number;
    date_start: string;
    date_end: string;
  };

  /** Datos para calcular tasa de ocupación próximos 15 días */
  occupancy_rate_15_days: {
    reserved_days: number;
    blocked_days: number;
    possible_days: number;
    date_start: string;
    date_end: string;
  };
}

interface UseDashboardAggregatesParams {
  year: number;
  month: number;
  propertyIds?: string[];
  organizationIds?: string[];
}

/**
 * Hook to fetch aggregated dashboard metrics using database function
 * Returns sums and counts for income, expenses, and occupancy data
 */
export const useDashboardAggregates = ({
  year,
  month,
  propertyIds,
  organizationIds,
}: UseDashboardAggregatesParams) => {
  return useQuery({
    queryKey: ["dashboard-aggregates", year, month, propertyIds, organizationIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_dashboard_metrics", {
        p_year: year,
        p_month: month,
        p_property_ids: propertyIds?.length ? propertyIds : null,
        p_organization_ids: organizationIds?.length ? organizationIds : null,
      });

      if (error) {
        console.error("Error fetching dashboard aggregates:", error);
        throw error;
      }

      return data as unknown as DashboardAggregates;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in memory for 10 minutes
  });
};
